import React, { useState, useMemo } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Dropdown, Badge, Modal, Alert } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";

// Données de démonstration pour les clés API
const initialApiKeys = [
  { 
    id: 1, 
    nom: "Application Mobile", 
    cle: "sk_live_abc123def456ghi789", 
    statut: "Active", 
    dateCreation: "15/10/2025 10:29", 
    dateExpiration: "15/01/2026 23:59",
    derniereUtilisation: "15/10/2025 14:30",
    utilisations: 1250,
    permissions: [],
    createdBy: "admin@entreprise.com",
    description: "Clé pour l'application mobile officielle"
  },
  { 
    id: 2, 
    nom: "Intégration CRM", 
    cle: "sk_live_xyz789uvw456rst123", 
    statut: "Active", 
    dateCreation: "14/10/2025 16:45", 
    dateExpiration: "14/02/2026 23:59",
    derniereUtilisation: "15/10/2025 09:15",
    utilisations: 876,
    permissions: ["webhooks:read", "webhooks:write"],
    createdBy: "tech@entreprise.com",
    description: "Intégration avec le système CRM Salesforce"
  },
  { 
    id: 3, 
    nom: "Dashboard Analytics", 
    cle: "sk_live_mno456pqr789stu012", 
    statut: "Active", 
    dateCreation: "13/10/2025 11:20", 
    dateExpiration: "13/03/2026 23:59",
    derniereUtilisation: "14/10/2025 16:45",
    utilisations: 543,
    permissions: ["webhooks:read", "webhooks:write"],
    createdBy: "analytics@entreprise.com",
    description: "Accès en lecture pour le tableau de bord analytics"
  },
  { 
    id: 4, 
    nom: "Webhook Production", 
    cle: "sk_live_jkl345mno678pqr901", 
    statut: "Inactive", 
    dateCreation: "12/10/2025 09:15", 
    dateExpiration: "12/04/2026 23:59",
    derniereUtilisation: "12/10/2025 14:30",
    utilisations: 0,
    permissions: ["webhooks:read", "webhooks:write"],
    createdBy: "webmaster@entreprise.com",
    description: "Clé pour les webhooks de production"
  },
  { 
    id: 5, 
    nom: "Test Développement", 
    cle: "sk_test_123abc456def789ghi", 
    statut: "Active", 
    dateCreation: "11/10/2025 14:30", 
    dateExpiration: "11/05/2026 23:59",
    derniereUtilisation: "11/10/2025 16:20",
    utilisations: 234,
    permissions: ["sms:send", "contacts:read", "contacts:write", "templates:read", "templates:write"],
    createdBy: "dev@entreprise.com",
    description: "Environnement de test et développement"
  },
  { 
    id: 6, 
    nom: "Backup System", 
    cle: "sk_live_backup123system456", 
    statut: "Active", 
    dateCreation: "10/10/2025 16:15", 
    dateExpiration: "10/06/2026 23:59",
    derniereUtilisation: "15/10/2025 08:00",
    utilisations: 89,
    permissions: ["sms:read", "contacts:read", "campaigns:read", "reports:read"],
    createdBy: "admin@entreprise.com",
    description: "Clé de sauvegarde pour les systèmes de secours"
  },
  { 
    id: 7, 
    nom: "Partenaire Marketing", 
    cle: "sk_live_partner789market456", 
    statut: "Révoquée", 
    dateCreation: "09/10/2025 10:00", 
    dateExpiration: "09/07/2026 23:59",
    derniereUtilisation: "14/10/2025 11:30",
    utilisations: 156,
    permissions: ["sms:send", "campaigns:read"],
    createdBy: "admin@entreprise.com",
    description: "Accès partenaire pour agence marketing",
    raisonRevocation: "Contrat terminé"
  },
  { 
    id: 8, 
    nom: "Intégration ERP", 
    cle: "sk_live_erp123integration456", 
    statut: "Expirée", 
    dateCreation: "08/10/2025 08:45", 
    dateExpiration: "08/08/2025 23:59",
    derniereUtilisation: "08/10/2025 14:15",
    utilisations: 67,
    permissions: ["sms:send", "contacts:read", "contacts:write", "reports:read"],
    createdBy: "erp@entreprise.com",
    description: "Intégration avec le système ERP SAP"
  }
];

// Permissions disponibles
const permissionsDisponibles = [
  "sms:send",
  "sms:read",
  "contacts:read",
  "contacts:write",
  "templates:read",
  "templates:write",
  "campaigns:read",
  "campaigns:write",
  "analytics:read",
  "reports:read",
  "reports:write",
  "webhooks:read",
  "webhooks:write",
  "account:read",
  "billing:read"
];

// Statuts disponibles
const statutsDisponibles = [
  "Active",
  "Inactive",
  "Révoquée",
  "Expirée",
  "En attente"
];

export default function ApiKeysManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('dateCreation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [apiKeys, setApiKeys] = useState(initialApiKeys);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedStatutFilter, setSelectedStatutFilter] = useState('all');
  const [newApiKey, setNewApiKey] = useState('');
  
  // États pour le formulaire de création/édition
  const [apiKeyForm, setApiKeyForm] = useState({
    nom: '',
    description: '',
    dateExpiration: '',
    permissions: [],
    createdBy: ''
  });

  // Filtrage et tri des données
  const filteredAndSortedItems = useMemo(() => {
    let filtered = apiKeys.filter(key => {
      const matchesSearch = 
        key.nom.toLowerCase().includes(filterText.toLowerCase()) ||
        key.cle.toLowerCase().includes(filterText.toLowerCase()) ||
        key.description.toLowerCase().includes(filterText.toLowerCase()) ||
        key.createdBy.toLowerCase().includes(filterText.toLowerCase());
      
      const matchesStatut = selectedStatutFilter === 'all' || key.statut === selectedStatutFilter;
      
      return matchesSearch && matchesStatut;
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'dateExpiration') {
        aValue = new Date(a.dateExpiration);
        bValue = new Date(b.dateExpiration);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [filterText, sortField, sortDirection, selectedStatutFilter, apiKeys]);

  // Statistiques
  const stats = useMemo(() => {
    const total = apiKeys.length;
    const actives = apiKeys.filter(k => k.statut === "Active").length;
    const inactives = apiKeys.filter(k => k.statut === "Inactive").length;
    const revoquees = apiKeys.filter(k => k.statut === "Révoquée").length;
    const expirees = apiKeys.filter(k => k.statut === "Expirée").length;
    const totalUtilisations = apiKeys.reduce((sum, k) => sum + k.utilisations, 0);
    
    return { total, actives, inactives, revoquees, expirees, totalUtilisations };
  }, [apiKeys]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedKeys(currentItems.map(key => key.id));
    } else {
      setSelectedKeys([]);
    }
  };

  const handleSelectKey = (keyId) => {
    setSelectedKeys(prev => 
      prev.includes(keyId) 
        ? prev.filter(id => id !== keyId)
        : [...prev, keyId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedKeys.length === currentItems.length;
  const isSomeSelected = selectedKeys.length > 0 && selectedKeys.length < currentItems.length;

  // Générer une nouvelle clé API
  const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = 'sk_live_';
    for (let i = 0; i < 32; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  };

  // Gestion des clés API
  const handleCreateApiKey = () => {
    if (!apiKeyForm.nom.trim() || !apiKeyForm.dateExpiration) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const newKey = generateApiKey();
    const newApiKeyData = {
      id: Math.max(...apiKeys.map(k => k.id)) + 1,
      nom: apiKeyForm.nom,
      cle: newKey,
      statut: "Active",
      dateCreation: new Date().toLocaleString('fr-FR'),
      dateExpiration: apiKeyForm.dateExpiration + " 23:59",
      derniereUtilisation: "",
      utilisations: 0,
      permissions: apiKeyForm.permissions,
      createdBy: apiKeyForm.createdBy || "admin@entreprise.com",
      description: apiKeyForm.description
    };

    setApiKeys(prev => [newApiKeyData, ...prev]);
    setNewApiKey(newKey);
    setApiKeyForm({
      nom: '',
      description: '',
      dateExpiration: '',
      permissions: [],
      createdBy: ''
    });
    setShowCreateModal(false);
    setShowKeyModal(true);
  };

  const handleEditApiKey = (key) => {
    setSelectedKey(key);
    setApiKeyForm({
      nom: key.nom,
      description: key.description,
      dateExpiration: key.dateExpiration.split(' ')[0],
      permissions: [...key.permissions],
      createdBy: key.createdBy
    });
    setShowEditModal(true);
  };

  const handleUpdateApiKey = () => {
    if (!apiKeyForm.nom.trim() || !apiKeyForm.dateExpiration) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setApiKeys(prev => 
      prev.map(k => 
        k.id === selectedKey.id 
          ? { 
              ...k, 
              nom: apiKeyForm.nom,
              description: apiKeyForm.description,
              dateExpiration: apiKeyForm.dateExpiration + " 23:59",
              permissions: apiKeyForm.permissions,
              createdBy: apiKeyForm.createdBy
            }
          : k
      )
    );
    
    setShowEditModal(false);
    setSelectedKey(null);
    setApiKeyForm({
      nom: '',
      description: '',
      dateExpiration: '',
      permissions: [],
      createdBy: ''
    });
    
    alert(`Clé API "${apiKeyForm.nom}" mise à jour avec succès`);
  };

  const handleShowDetails = (key) => {
    setSelectedKey(key);
    setShowDetailsModal(true);
  };

  const handleShowKey = (key) => {
    setSelectedKey(key);
    setShowKeyModal(true);
  };

  const handleToggleStatus = (keyId, newStatus) => {
    setApiKeys(prev => 
      prev.map(k => 
        k.id === keyId 
          ? { ...k, statut: newStatus }
          : k
      )
    );
  };

  const handleBulkToggleStatus = (newStatus) => {
    if (selectedKeys.length === 0) {
      alert("Veuillez sélectionner au moins une clé API");
      return;
    }

    setApiKeys(prev => 
      prev.map(k => 
        selectedKeys.includes(k.id) 
          ? { ...k, statut: newStatus }
          : k
      )
    );
    
    setSelectedKeys([]);
    alert(`${selectedKeys.length} clé(s) API ${newStatus === "Active" ? "activée(s)" : "désactivée(s)"}`);
  };

  const handleRegenerateKey = (keyId) => {
    if (window.confirm("Êtes-vous sûr de vouloir régénérer cette clé API ? L'ancienne clé ne fonctionnera plus.")) {
      const newKey = generateApiKey();
      setApiKeys(prev => 
        prev.map(k => 
          k.id === keyId 
            ? { 
                ...k, 
                cle: newKey,
                dateCreation: new Date().toLocaleString('fr-FR'),
                utilisations: 0,
                derniereUtilisation: ""
              }
            : k
        )
      );
      setNewApiKey(newKey);
      setShowKeyModal(true);
      alert("Clé API régénérée avec succès");
    }
  };

  const handleDeleteApiKey = (keyId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer définitivement cette clé API ?")) {
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      alert("Clé API supprimée avec succès");
    }
  };

  const handleBulkDelete = () => {
    if (selectedKeys.length === 0) {
      alert("Veuillez sélectionner au moins une clé API");
      return;
    }

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement ${selectedKeys.length} clé(s) API ?`)) {
      setApiKeys(prev => prev.filter(k => !selectedKeys.includes(k.id)));
      setSelectedKeys([]);
      alert(`${selectedKeys.length} clé(s) API supprimée(s) avec succès`);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Clé API copiée dans le presse-papier");
  };

  const handlePermissionToggle = (permission) => {
    setApiKeyForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  // Fonctions d'export
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedKeys.length > 0
      ? apiKeys.filter(key => selectedKeys.includes(key.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) { 
      alert("Aucune donnée à exporter"); 
      setExportLoading(false); 
      return; 
    }

    // Masquer les clés API sensibles pour l'export
    const safeData = dataToExport.map(key => ({
      ...key,
      cle: key.cle.substring(0, 8) + '...' + key.cle.substring(key.cle.length - 4)
    }));

    if (format === "csv") {
      const headers = ["Nom", "Clé API", "Statut", "Date Création", "Date Expiration", "Utilisations", "Permissions", "Créé par", "Description"];
      const csvContent = [
        "\uFEFF" + headers.join(";"),
        ...safeData.map(k => [
          k.nom,
          k.cle,
          k.statut,
          k.dateCreation,
          k.dateExpiration,
          k.utilisations,
          k.permissions.join(', '),
          k.createdBy,
          k.description
        ].map(v=>`"${v}"`).join(";"))
      ].join("\n");
      downloadFile(csvContent, "text/csv;charset=utf-8;", `export-clés-api-${Date.now()}.csv`);

    } else if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(safeData.map(k => ({
        Nom: k.nom,
        "Clé API": k.cle,
        Statut: k.statut,
        "Date Création": k.dateCreation,
        "Date Expiration": k.dateExpiration,
        Utilisations: k.utilisations,
        Permissions: k.permissions.join(', '),
        "Créé par": k.createdBy,
        Description: k.description
      })));
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Clés API");
      XLSX.writeFile(wb, `export-clés-api-${Date.now()}.xlsx`);

    } else if (format === "json") {
      downloadFile(JSON.stringify(safeData, null, 2), "application/json;charset=utf-8;", `export-clés-api-${Date.now()}.json`);
    }

    setExportLoading(false);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <i 
          className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill position-absolute ms-1`}
          style={{ fontSize: '12px' }}
        ></i>
      )}
    </th>
  );

  // Obtenir la couleur du badge selon le statut
  const getStatutVariant = (statut) => {
    switch (statut) {
      case 'Active': return 'success';
      case 'Inactive': return 'secondary';
      case 'Révoquée': return 'danger';
      case 'Expirée': return 'warning';
      case 'En attente': return 'info';
      default: return 'secondary';
    }
  };

  // Vérifier si une clé est expirée
  const isExpired = (dateExpiration) => {
    return new Date(dateExpiration) < new Date();
  };

  return (
    <div className="main main-app p-3 p-lg-4">
      <div className="container-fluid">
        <Row className="mb-4">
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-1 fw-bold">Gestion des Clés API</h4>
                <p className="text-muted mb-0">
                  Gérez les clés d'accès à l'API de votre plateforme SMS
                </p>
              </div>
              <div className="d-flex gap-2">
                {selectedKeys.length > 0 && (
                  <>
                    <Button 
                      variant="outline-success" 
                      size="sm"
                      onClick={() => handleBulkToggleStatus('Active')}
                    >
                      <i className="bi bi-play-circle me-2"></i>
                      Activer ({selectedKeys.length})
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => handleBulkToggleStatus('Inactive')}
                    >
                      <i className="bi bi-pause-circle me-2"></i>
                      Désactiver ({selectedKeys.length})
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={handleBulkDelete}
                    >
                      <i className="bi bi-trash me-2"></i>
                      Supprimer ({selectedKeys.length})
                    </Button>
                  </>
                )}
                <Dropdown>
                  <Dropdown.Toggle 
                    variant="outline-primary" 
                    disabled={exportLoading}
                  >
                    <i className={`bi ${exportLoading ? 'bi-arrow-repeat spin' : 'bi-download'} me-2`}></i>
                    {exportLoading ? 'Export...' : 'Exporter'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleExport('csv')} disabled={exportLoading}>
                      <i className="bi bi-file-earmark-spreadsheet me-2"></i>Export CSV
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleExport('excel')} disabled={exportLoading}>
                      <i className="bi bi-file-earmark-excel me-2"></i>Export Excel
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleExport('json')} disabled={exportLoading}>
                      <i className="bi bi-file-earmark-code me-2"></i>Export JSON
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Button 
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Nouvelle Clé API
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Statistiques */}
       <Row className="mb-4">
  <Col md={3} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-primary text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-key me-2 fs-6"></i>
          <span className="small fw-semibold">TOTAL CLÉS</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.total}</h3>
          <small className="text-muted">Clés générées</small>
        </div>
      </Card.Body>
    </Card>
  </Col>

  <Col md={3} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-success text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-check-circle me-2 fs-6"></i>
          <span className="small fw-semibold">CLÉS ACTIVES</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.actives}</h3>
          <small className="text-muted">En service</small>
        </div>
      </Card.Body>
    </Card>
  </Col>

  <Col md={3} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-secondary text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-pause-circle me-2 fs-6"></i>
          <span className="small fw-semibold">CLÉS INACTIVES</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.inactives}</h3>
          <small className="text-muted">En pause</small>
        </div>
      </Card.Body>
    </Card>
  </Col>

  <Col md={3} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-danger text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-x-circle me-2 fs-6"></i>
          <span className="small fw-semibold">RÉVOQUÉES/EXPIRÉES</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.revoquees + stats.expirees}</h3>
          <small className="text-muted">Non utilisables</small>
        </div>
      </Card.Body>
    </Card>
  </Col>

  {/* <Col md={4} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-info text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-bar-chart me-2 fs-6"></i>
          <span className="small fw-semibold">UTILISATIONS TOTALES</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.totalUtilisations}</h3>
          <small className="text-muted">Total d'utilisations</small>
        </div>
      </Card.Body>
    </Card>
  </Col> */}
</Row>
        <Row>
          <Col md={12}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-key me-2 fs-5"></i>
                  <span className="fw-semibold">Liste des Clés API</span>
                </div>
                <div className="text-white-50 small d-flex align-items-center">
                  <i className="bi bi-info-circle me-1"></i>
                  <span>{filteredAndSortedItems.length} clé(s) API</span>
                  {selectedKeys.length > 0 && (
                    <span className="ms-2">• {selectedKeys.length} sélectionnée(s)</span>
                  )}
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                {/* Barre d'outils */}
                <div className="p-3 border-bottom bg-light">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-white">
                          <i className="bi bi-search text-muted"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher par nom, clé API, description..."
                          value={filterText}
                          onChange={e => setFilterText(e.target.value)}
                          className="border-start-0"
                        />
                        {filterText && (
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => setFilterText('')}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={6} className="text-end">
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="bi bi-filter me-2"></i>
                            {selectedStatutFilter === 'all' ? 'Tous les statuts' : selectedStatutFilter}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setSelectedStatutFilter('all')}>
                              Tous les statuts
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            {statutsDisponibles.map(statut => (
                              <Dropdown.Item 
                                key={statut}
                                onClick={() => setSelectedStatutFilter(statut)}
                              >
                                <Badge bg={getStatutVariant(statut)} className="me-2">
                                  {statut}
                                </Badge>
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>

                        <Button variant="outline-secondary" size="sm" className="me-2">
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Actualiser
                        </Button>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="bi bi-list-ul me-2"></i>
                            {itemsPerPage} par page
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setItemsPerPage(10)}>10 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(25)}>25 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(50)}>50 par page</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Tableau */}
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-secondary text-white">
                      <tr>
                        <th style={{ width: '40px' }}>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer'
                              }}
                              checked={isAllSelected}
                              ref={input => {
                                if (input) {
                                  input.indeterminate = isSomeSelected;
                                }
                              }}
                              onChange={handleSelectAll}
                            />
                          </div>
                        </th>
                        <SortableHeader field="nom">NOM</SortableHeader>
                        <th>CLÉ API</th>
                        <SortableHeader field="statut">STATUT</SortableHeader>
                       
                        {/* <SortableHeader field="utilisations">UTILIS.</SortableHeader> */}
                        <SortableHeader field="dateCreation">CRÉÉE LE</SortableHeader>
                        <SortableHeader field="dateExpiration">EXPIRE LE</SortableHeader>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((key) => (
                          <tr key={key.id}>
                            <td>
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                  }}
                                  checked={selectedKeys.includes(key.id)}
                                  onChange={() => handleSelectKey(key.id)}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="fw-bold">{key.nom}</div>
                              <small className="text-muted">{key.description}</small>
                            </td>
                            <td>
                              <div className="font-monospace small">
                                {key.cle.substring(0, 12)}...{key.cle.substring(key.cle.length - 4)}
                              </div>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                className="mt-1"
                                onClick={() => handleCopyToClipboard(key.cle)}
                              >
                                <i className="bi bi-clipboard"></i>
                              </Button>
                            </td>
                            <td>
                              <Badge 
                                bg={getStatutVariant(key.statut)} 
                                className="d-flex align-items-center justify-content-center"
                                style={{ minWidth: "90px" }}
                              >
                                <i className={`bi ${
                                  key.statut === "Active" ? "bi-check-circle" :
                                  key.statut === "Inactive" ? "bi-pause-circle" :
                                  key.statut === "Révoquée" ? "bi-x-circle" :
                                  key.statut === "Expirée" ? "bi-clock" : "bi-hourglass"
                                } me-1`}></i>
                                {key.statut}
                                {isExpired(key.dateExpiration) && key.statut === "Active" && (
                                  <i className="bi bi-exclamation-triangle ms-1" title="Clé expirée"></i>
                                )}
                              </Badge>
                            </td>
                           
                            {/* <td>
                              <div className="text-center">
                                <Badge bg="info" className="fw-normal">
                                  {key.utilisations}
                                </Badge>
                                {key.derniereUtilisation && (
                                  <small className="d-block text-muted">
                                    {new Date(key.derniereUtilisation).toLocaleDateString('fr-FR')}
                                  </small>
                                )}
                              </div>
                            </td> */}
                            <td>
                              <small className="text-muted">{key.dateCreation}</small>
                            </td>
                            <td>
                              <small className={isExpired(key.dateExpiration) ? "text-danger fw-bold" : "text-muted"}>
                                {key.dateExpiration}
                                {isExpired(key.dateExpiration) && (
                                  <i className="bi bi-exclamation-triangle ms-1" title="Clé expirée"></i>
                                )}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button 
                                  variant="outline-info" 
                                  size="sm"
                                  title="Voir les détails"
                                  onClick={() => handleShowDetails(key)}
                                >
                                  <i className="bi bi-eye"></i>
                                </Button>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  title="Modifier"
                                  onClick={() => handleEditApiKey(key)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                <Button 
                                  variant="outline-warning" 
                                  size="sm"
                                  title="Régénérer la clé"
                                  onClick={() => handleRegenerateKey(key.id)}
                                >
                                  <i className="bi bi-arrow-repeat"></i>
                                </Button>
                                <Button 
                                  variant="outline-success" 
                                  size="sm"
                                  title="Voir la clé complète"
                                  onClick={() => handleShowKey(key)}
                                >
                                  <i className="bi bi-key"></i>
                                </Button>
                                {key.statut === "Active" ? (
                                  <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    title="Désactiver"
                                    onClick={() => handleToggleStatus(key.id, "Inactive")}
                                  >
                                    <i className="bi bi-pause"></i>
                                  </Button>
                                ) : (
                                  <Button 
                                    variant="outline-success" 
                                    size="sm"
                                    title="Activer"
                                    onClick={() => handleToggleStatus(key.id, "Active")}
                                  >
                                    <i className="bi bi-play"></i>
                                  </Button>
                                )}
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  title="Supprimer"
                                  onClick={() => handleDeleteApiKey(key.id)}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center py-5">
                            <i className="bi bi-key display-4 text-muted"></i>
                            <p className="mt-3 text-muted">AUCUNE CLÉ API TROUVÉE.</p>
                            <p className="text-muted mb-3">
                              Aucune clé API ne correspond aux critères de recherche.
                            </p>
                            {(filterText || selectedStatutFilter !== 'all') && (
                              <div>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => setFilterText('')}
                                  className="me-2"
                                >
                                  Réinitialiser la recherche
                                </Button>
                                <Button 
                                  variant="outline-warning" 
                                  size="sm"
                                  onClick={() => setSelectedStatutFilter('all')}
                                >
                                  Afficher tous les statuts
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-3 border-top d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} sur {filteredAndSortedItems.length} clés API
                      {selectedKeys.length > 0 && (
                        <span className="ms-2 text-primary">• {selectedKeys.length} sélectionnée(s)</span>
                      )}
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.Prev 
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      />
                      {[...Array(totalPages)].map((_, index) => (
                        <Pagination.Item
                          key={index + 1}
                          active={index + 1 === currentPage}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next 
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Modal de création de clé API */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-plus-circle me-2"></i>
            Créer une nouvelle clé API
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Les clés API permettent aux applications tierces d'accéder à votre compte de manière sécurisée.
          </Alert>

          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom de la clé *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: Application Mobile"
                    value={apiKeyForm.nom}
                    onChange={(e) => setApiKeyForm({...apiKeyForm, nom: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date d'expiration *</Form.Label>
                  <Form.Control
                    type="date"
                    value={apiKeyForm.dateExpiration}
                    onChange={(e) => setApiKeyForm({...apiKeyForm, dateExpiration: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Description de l'utilisation de cette clé API..."
                value={apiKeyForm.description}
                onChange={(e) => setApiKeyForm({...apiKeyForm, description: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Créé par</Form.Label>
              <Form.Control
                type="email"
                placeholder="email@entreprise.com"
                value={apiKeyForm.createdBy}
                onChange={(e) => setApiKeyForm({...apiKeyForm, createdBy: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Permissions</Form.Label>
              <div className="border rounded p-3 bg-light">
                <Row>
                  {permissionsDisponibles.map((permission, index) => (
                    <Col key={index} md={6} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`perm-${index}`}
                        label={
                          <Badge bg="light" text="dark" className="ms-2">
                            {permission}
                          </Badge>
                        }
                        checked={apiKeyForm.permissions.includes(permission)}
                        onChange={() => handlePermissionToggle(permission)}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
              <Form.Text className="text-muted">
                Sélectionnez les permissions accordées à cette clé API
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateApiKey}
            disabled={!apiKeyForm.nom.trim() || !apiKeyForm.dateExpiration}
          >
            <i className="bi bi-check-circle me-2"></i>
            Créer la clé API
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal d'affichage de la clé API */}
      <Modal show={showKeyModal} onHide={() => setShowKeyModal(false)} centered>
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title>
            <i className="bi bi-key me-2"></i>
            Clé API Générée
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <i className="bi bi-key-fill display-4 text-warning mb-3"></i>
          <h5 className="fw-bold mb-3">Votre clé API a été générée</h5>
          <Alert variant="warning" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Cette clé ne sera affichée qu'une seule fois. Assurez-vous de la copier et de la stocker en sécurité.
          </Alert>
          <div className="bg-dark text-light p-3 rounded font-monospace mb-3">
            {newApiKey || selectedKey?.cle}
          </div>
          <Button 
            variant="warning" 
            onClick={() => handleCopyToClipboard(newApiKey || selectedKey?.cle)}
            className="me-2"
          >
            <i className="bi bi-clipboard me-2"></i>
            Copier la clé
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowKeyModal(false)}>
            Fermer
          </Button>
        </Modal.Body>
      </Modal>

      {/* Modal de détails de la clé API */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-info-circle me-2"></i>
            Détails de la clé API
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedKey && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <h4>{selectedKey.nom}</h4>
                  <p className="text-muted">{selectedKey.description}</p>
                </Col>
                <Col md={6}>
                  <div className="border rounded p-3">
                    <div className="row small">
                      <div className="col-6 text-muted">Statut:</div>
                      <div className="col-6">
                        <Badge bg={getStatutVariant(selectedKey.statut)}>
                          {selectedKey.statut}
                        </Badge>
                      </div>
                      <div className="col-6 text-muted">Utilisations:</div>
                      <div className="col-6 fw-bold">{selectedKey.utilisations}</div>
                      <div className="col-6 text-muted">Créée par:</div>
                      <div className="col-6">{selectedKey.createdBy}</div>
                      <div className="col-6 text-muted">Créée le:</div>
                      <div className="col-6">{selectedKey.dateCreation}</div>
                      <div className="col-6 text-muted">Expire le:</div>
                      <div className="col-6">{selectedKey.dateExpiration}</div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <h6>Clé API</h6>
                  <div className="border rounded p-3 bg-light font-monospace small">
                    {selectedKey.cle}
                  </div>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => handleCopyToClipboard(selectedKey.cle)}
                  >
                    <i className="bi bi-clipboard me-2"></i>
                    Copier la clé
                  </Button>
                </Col>
                <Col md={6}>
                  <h6>Dernière utilisation</h6>
                  <div className="border rounded p-3">
                    {selectedKey.derniereUtilisation ? (
                      <div>
                        <div className="fw-bold">{selectedKey.derniereUtilisation}</div>
                        <small className="text-muted">Dernier accès</small>
                      </div>
                    ) : (
                      <div className="text-muted">Aucune utilisation enregistrée</div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <h6>Permissions</h6>
                  <div className="border rounded p-3">
                    <div className="d-flex flex-wrap gap-2">
                      {selectedKey.permissions.map((permission, index) => (
                        <Badge key={index} bg="primary" className="fs-6">
                          {permission}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>

              {selectedKey.raisonRevocation && (
                <Row className="mt-3">
                  <Col md={12}>
                    <Alert variant="danger">
                      <h6>Raison de la révocation</h6>
                      <p className="mb-0">{selectedKey.raisonRevocation}</p>
                    </Alert>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowDetailsModal(false);
              handleEditApiKey(selectedKey);
            }}
          >
            <i className="bi bi-pencil me-2"></i>
            Modifier
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CSS pour l'animation de chargement */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .font-monospace {
          font-family: 'Courier New', monospace;
        }
      `}</style>
      <Footer />
    </div>
  );
}